import React, { useEffect, useRef } from 'react';
import ModalOverlay from './ModalOverlay';
import ModalPanel from './ModalPanel';
import { MODAL_SECTIONS, getModalSectionTheme } from './modalTheme';
import { globalModalManager } from '../Modal';
import { filterActiveEmployees } from '../../utils/employeeStatusUtils';
import { Wallet } from 'lucide-react';
import AppSelect from './AppSelect';

const FOCUS_RING = {
  [MODAL_SECTIONS.LABOUR]: 'focus:border-teal-600 focus:ring-teal-600/30',
  [MODAL_SECTIONS.EXPENSES_FIXED]: 'focus:border-indigo-600 focus:ring-indigo-600/30',
  [MODAL_SECTIONS.EXPENSES_VARIABLE]: 'focus:border-violet-600 focus:ring-violet-600/30',
};

const SUMMARY_BOX = {
  [MODAL_SECTIONS.LABOUR]: 'border-teal-200/80 bg-teal-50/50',
  [MODAL_SECTIONS.EXPENSES_FIXED]: 'border-indigo-200/80 bg-indigo-50/50',
  [MODAL_SECTIONS.EXPENSES_VARIABLE]: 'border-violet-200/80 bg-violet-50/50',
};

const fieldClass = (section) =>
  `w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 ${
    FOCUS_RING[section] || FOCUS_RING[MODAL_SECTIONS.LABOUR]
  }`;

/**
 * Shared modal to settle a payable (labour/advance, fixed expense, variable expense) from an employee wallet.
 */
export default function SettleFromWalletModal({
  open,
  modalSection = MODAL_SECTIONS.LABOUR,
  summaryPrimary,
  summarySecondary,
  amount,
  employees,
  companyCurrency,
  settleDate,
  setSettleDate,
  settlePaymentMode,
  setSettlePaymentMode,
  settleEmployeeId,
  setSettleEmployeeId,
  settleEmployeeBalance,
  settleRemark,
  setSettleRemark,
  submitting,
  onClose,
  onSubmit,
}) {
  const modalIdRef = useRef(`modal-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    if (!open) return undefined;
    globalModalManager.register(modalIdRef.current, onClose);
    return () => globalModalManager.unregister(modalIdRef.current);
  }, [open, onClose]);

  if (!open) return null;

  const available =
    settlePaymentMode === 'upi'
      ? settleEmployeeBalance?.upi || 0
      : settleEmployeeBalance?.cash || 0;
  const amountNum = Number(amount) || 0;
  const insufficient = available < amountNum;
  const activeEmployees = filterActiveEmployees(employees);
  const summaryBox = SUMMARY_BOX[modalSection] || SUMMARY_BOX[MODAL_SECTIONS.LABOUR];
  const sectionTheme = getModalSectionTheme(modalSection);

  return (
    <ModalOverlay open onClose={onClose} zIndexClass="z-[100]" suppressEscapeKey>
      <ModalPanel
        section={modalSection}
        title={
          <span className="inline-flex items-center gap-2">
            <Wallet className="h-5 w-5 shrink-0" aria-hidden />
            Settle from wallet
          </span>
        }
        titleId="settle-from-wallet-title"
        onClose={onClose}
        sizeClass="max-w-md"
        bodyClassName="!p-0"
      >
        <form onSubmit={onSubmit} className="p-5 space-y-4">
          <div className={`rounded-xl border p-4 text-sm ${summaryBox}`}>
            <p className="font-semibold text-gray-900">{summaryPrimary}</p>
            {summarySecondary && <p className="mt-1 text-gray-600">{summarySecondary}</p>}
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-600">
              Settlement date (can be back-dated) *
            </label>
            <input
              type="date"
              value={settleDate}
              onChange={(e) => setSettleDate(e.target.value)}
              className={fieldClass(modalSection)}
              required
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-600">
              Deduct from *
            </label>
            <AppSelect
              value={settlePaymentMode}
              onChange={(e) => setSettlePaymentMode(e.target.value)}
              aria-label="Deduct from"
              sheetTitle="Deduct from"
              options={[
                { value: 'cash', label: 'Cash' },
                { value: 'upi', label: 'UPI' },
              ]}
              triggerClassName={`${fieldClass(modalSection)} min-h-[44px] flex items-center justify-between text-left touch-manipulation`}
            />
            <p className="mt-1 text-xs text-gray-500">Deduct from the employee&apos;s Cash or UPI balance.</p>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-600">
              Employee (wallet) *
            </label>
            <AppSelect
              value={settleEmployeeId}
              onChange={(e) => setSettleEmployeeId(e.target.value)}
              placeholder="Select employee"
              aria-label="Employee wallet"
              sheetTitle="Employee (wallet)"
              options={[
                { value: '', label: 'Select employee' },
                ...activeEmployees.map((emp) => {
                  const bal = emp.cashBalance || { cash: 0, upi: 0 };
                  return {
                    value: emp.id,
                    label: `${emp.name} (${emp.employeeId || 'N/A'}) – Cash: ₹${bal.cash?.toLocaleString('en-IN') || 0} | UPI: ₹${bal.upi?.toLocaleString('en-IN') || 0}`,
                  };
                }),
              ]}
              triggerClassName={`${fieldClass(modalSection)} min-h-[44px] flex items-center justify-between text-left touch-manipulation`}
            />
            {settleEmployeeId && settleEmployeeBalance && (
              <p className="mt-2 text-xs text-gray-600">
                Available ({settlePaymentMode === 'upi' ? 'UPI' : 'Cash'}): ₹
                {available.toLocaleString('en-IN')}
                {insufficient && <span className="ml-1 font-medium text-red-600">(Insufficient)</span>}
              </p>
            )}
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-600">
              Remark (optional)
            </label>
            <input
              type="text"
              value={settleRemark}
              onChange={(e) => setSettleRemark(e.target.value)}
              placeholder="e.g. Back-dated settlement"
              className={fieldClass(modalSection)}
            />
          </div>

          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 min-h-[44px] sm:min-h-0 rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 touch-manipulation"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className={`flex-1 min-h-[44px] sm:min-h-0 rounded-lg px-4 py-2.5 text-sm font-semibold text-white shadow-md hover:brightness-105 disabled:opacity-50 touch-manipulation ${sectionTheme.btnPrimary}`}
            >
              {submitting ? 'Settling…' : 'Confirm settlement'}
            </button>
          </div>
        </form>
      </ModalPanel>
    </ModalOverlay>
  );
}
